import { Plus, Trash2 } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { useCreateDealerUser, useSetStoreMember } from "@/api/mutations";
import { useStores, useUsers } from "@/api/queries";
import type { DealerCredentials, EntityRef, OrgRole, StoreRole } from "@/api/types";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { NativeSelect, NativeSelectOption } from "@/components/ui/native-select";
import { Spinner } from "@/components/ui/spinner";
import { formErrorsFrom } from "@/lib/form-errors";
import { ROLE_LABEL } from "@/lib/status";

export const ORG_ROLES: OrgRole[] = ["MEMBER", "OWNER", "ADMIN", "GROUP_MANAGER"];
export const STORE_ROLES: StoreRole[] = ["BUYER", "STORE_MANAGER", "BDC", "APPRAISER", "VIEWER"];
const ORG_ROLE_HINT: Record<OrgRole, string> = {
  MEMBER: "Works only in the stores you give them below.",
  OWNER: "Every store and every permission, including members.",
  ADMIN: "Every store and every permission, including members.",
  GROUP_MANAGER: "Runs every store; doesn't manage membership.",
};

/** Dealer accounts: dealer-web does not force a password change yet, so say so honestly. */
export const DEALER_PASSWORD_NOTE = "They sign in to the dealer app with it; ask them to change it after their first sign-in.";

const ALL_STORES = { page: 1, pageSize: 200, sort: "name", order: "asc" as const };

/** Every store of an organization, for pickers (no query until an organization is known). */
function useOrgStores(organizationId: string | undefined) {
  return useStores({ ...ALL_STORES, filters: { organizationId: organizationId ?? "" } }, Boolean(organizationId));
}

/** New dealer account in an organization, with its organization role and store access. */
export function CreateUserDialog({
  organization,
  open,
  onOpenChange,
  onCreated,
}: {
  organization: EntityRef;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated: (result: DealerCredentials) => void;
}) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState<OrgRole>("MEMBER");
  const [access, setAccess] = useState<Array<{ storeId: string; role: StoreRole }>>([]);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const stores = useOrgStores(open ? organization.id : undefined);
  const create = useCreateDealerUser(organization.id);
  useEffect(() => {
    if (open) {
      setFirstName("");
      setLastName("");
      setEmail("");
      setPhone("");
      setRole("MEMBER");
      setAccess([]);
      setErrors({});
    }
  }, [open]);
  const available = (stores.data?.items ?? []).filter((s) => !access.some((a) => a.storeId === s.id));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!firstName.trim()) next.firstName = "Enter the first name";
    if (!lastName.trim()) next.lastName = "Enter the last name";
    if (!z.email().safeParse(email.trim()).success) next.email = "Enter a valid email address";
    if (phone.trim() && phone.replace(/\D/g, "").length < 10) next.phone = "Enter a 10-digit US number or +country code";
    if (role === "MEMBER" && access.length === 0) next.stores = "Members need at least one store";
    if (Object.keys(next).length) return setErrors(next);
    setErrors({});
    create.mutate(
      { firstName: firstName.trim(), lastName: lastName.trim(), email: email.trim(), phone: phone.trim() || null, role, stores: access },
      {
        onSuccess: (r) => {
          onOpenChange(false);
          if (!r.temporaryPassword) toast.success(`${r.user.name} already had an account and was added to ${organization.name}`);
          onCreated(r);
        },
        onError: (err) => {
          const { fields, form } = formErrorsFrom(err);
          setErrors({ ...fields, form: form ?? undefined });
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={submit} noValidate>
          <DialogHeader>
            <DialogTitle>Add user to {organization.name}</DialogTitle>
            <DialogDescription>
              Creates a dealer account with a one-time temporary password. If the email already has an account, that person is added instead and keeps their
              password.
            </DialogDescription>
          </DialogHeader>
          <FieldGroup className="my-4 gap-3">
            {errors.form ? (
              <Alert variant="destructive">
                <AlertDescription>{errors.form}</AlertDescription>
              </Alert>
            ) : null}
            <div className="grid gap-3 sm:grid-cols-2">
              <Field data-invalid={Boolean(errors.firstName)} className="gap-1">
                <FieldLabel htmlFor="new-user-first-name">First name</FieldLabel>
                <Input
                  id="new-user-first-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  aria-invalid={Boolean(errors.firstName)}
                  autoComplete="off"
                />
                <FieldError className="text-xs">{errors.firstName}</FieldError>
              </Field>
              <Field data-invalid={Boolean(errors.lastName)} className="gap-1">
                <FieldLabel htmlFor="new-user-last-name">Last name</FieldLabel>
                <Input
                  id="new-user-last-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  aria-invalid={Boolean(errors.lastName)}
                  autoComplete="off"
                />
                <FieldError className="text-xs">{errors.lastName}</FieldError>
              </Field>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field data-invalid={Boolean(errors.email)} className="gap-1">
                <FieldLabel htmlFor="new-user-email">Email</FieldLabel>
                <Input
                  id="new-user-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-invalid={Boolean(errors.email)}
                  autoComplete="off"
                />
                <FieldError className="text-xs">{errors.email}</FieldError>
              </Field>
              <Field data-invalid={Boolean(errors.phone)} className="gap-1">
                <FieldLabel htmlFor="new-user-phone">Phone (optional)</FieldLabel>
                <Input
                  id="new-user-phone"
                  type="tel"
                  placeholder="(562) 555-0100"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  aria-invalid={Boolean(errors.phone)}
                  autoComplete="off"
                />
                {!errors.phone ? <FieldDescription className="text-xs">Must be unique across dealer accounts.</FieldDescription> : null}
                <FieldError className="text-xs">{errors.phone}</FieldError>
              </Field>
            </div>
            <Field className="gap-1">
              <FieldLabel htmlFor="new-user-role">Organization role</FieldLabel>
              <NativeSelect id="new-user-role" value={role} onChange={(e) => setRole(e.target.value as OrgRole)} className="w-full">
                {ORG_ROLES.map((r) => (
                  <NativeSelectOption key={r} value={r}>
                    {ROLE_LABEL[r]}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
              <FieldDescription className="text-xs">{ORG_ROLE_HINT[role]}</FieldDescription>
            </Field>
            <Field data-invalid={Boolean(errors.stores)} className="gap-1.5">
              <FieldLabel>Store access</FieldLabel>
              {access.map((a, i) => (
                <div key={a.storeId} className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 truncate text-sm">{stores.data?.items.find((s) => s.id === a.storeId)?.name ?? a.storeId}</span>
                  <NativeSelect
                    size="sm"
                    aria-label={`Role at ${stores.data?.items.find((s) => s.id === a.storeId)?.name ?? "store"}`}
                    value={a.role}
                    onChange={(e) => setAccess((list) => list.map((x, j) => (j === i ? { ...x, role: e.target.value as StoreRole } : x)))}
                  >
                    {STORE_ROLES.map((r) => (
                      <NativeSelectOption key={r} value={r}>
                        {ROLE_LABEL[r]}
                      </NativeSelectOption>
                    ))}
                  </NativeSelect>
                  <Button
                    type="button"
                    size="icon-xs"
                    variant="ghost"
                    aria-label="Remove store"
                    onClick={() => setAccess((list) => list.filter((_, j) => j !== i))}
                  >
                    <Trash2 />
                  </Button>
                </div>
              ))}
              {available.length ? (
                <NativeSelect
                  aria-label="Add store access"
                  value=""
                  onChange={(e) => e.target.value && setAccess((list) => [...list, { storeId: e.target.value, role: "BUYER" }])}
                  className="w-full"
                >
                  <NativeSelectOption value="">{stores.isPending ? "Loading stores…" : "+ Give access to a store…"}</NativeSelectOption>
                  {available.map((s) => (
                    <NativeSelectOption key={s.id} value={s.id}>
                      {s.name}
                      {s.location ? ` — ${s.location}` : ""}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              ) : stores.data && stores.data.items.length === 0 ? (
                <p className="text-muted-foreground text-xs">This organization has no stores yet.</p>
              ) : null}
              <FieldError className="text-xs">{errors.stores}</FieldError>
            </Field>
          </FieldGroup>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? <Spinner data-icon="inline-start" /> : <Plus data-icon="inline-start" />}
              Add user
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Grant store access. Opened from a store (pick the person) or from a user (pick the store);
 * only members of the store's organization can be added. Render it only while open.
 */
export function AddStoreAccessDialog({
  onClose,
  store,
  user,
}: {
  onClose: () => void;
  /** Fixed store (from the store page) … */
  store?: { id: string; name: string; organization: EntityRef; memberIds: string[] };
  /** … or fixed user (from the user page) with the organizations they belong to. */
  user?: { id: string; name: string; organizations: EntityRef[]; storeIds: string[] };
}) {
  const [storeId, setStoreId] = useState(store?.id ?? "");
  const [userId, setUserId] = useState(user?.id ?? "");
  const [personName, setPersonName] = useState(user?.name ?? "");
  const [role, setRole] = useState<StoreRole>("BUYER");
  const [error, setError] = useState<string | null>(null);
  const setMember = useSetStoreMember(storeId);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!storeId || !userId) return setError(store ? "Pick a person" : "Pick a store");
    setMember.mutate(
      { userId, role },
      {
        onSuccess: (s) => {
          toast.success(`${personName || "User"} added to ${s.name}`);
          onClose();
        },
        onError: (err) => setError(formErrorsFrom(err).form ?? "Could not add access"),
      },
    );
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <form onSubmit={submit} noValidate>
          <DialogHeader>
            <DialogTitle>{store ? `Add member to ${store.name}` : `Add ${user?.name} to a store`}</DialogTitle>
            <DialogDescription>
              {store
                ? `People must belong to ${store.organization.name} first — add new people from the organization's Users tab.`
                : "Stores of the organizations this person belongs to."}
            </DialogDescription>
          </DialogHeader>
          <FieldGroup className="my-4 gap-3">
            {error ? (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
            {store ? (
              <PersonPicker
                organizationId={store.organization.id}
                exclude={store.memberIds}
                value={userId}
                onChange={(id, name) => {
                  setUserId(id);
                  setPersonName(name);
                }}
              />
            ) : user ? (
              <StorePicker organizations={user.organizations} exclude={user.storeIds} value={storeId} onChange={setStoreId} />
            ) : null}
            <Field className="gap-1">
              <FieldLabel htmlFor="access-role">Role at the store</FieldLabel>
              <NativeSelect id="access-role" value={role} onChange={(e) => setRole(e.target.value as StoreRole)} className="w-full">
                {STORE_ROLES.map((r) => (
                  <NativeSelectOption key={r} value={r}>
                    {ROLE_LABEL[r]}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
          </FieldGroup>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={setMember.isPending}>
              {setMember.isPending ? <Spinner data-icon="inline-start" /> : null}
              Add access
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PersonPicker({
  organizationId,
  exclude,
  value,
  onChange,
}: {
  organizationId: string;
  exclude: string[];
  value: string;
  onChange: (id: string, name: string) => void;
}) {
  const people = useUsers({ ...ALL_STORES, filters: { organizationId } });
  const options = (people.data?.items ?? []).filter((u) => !exclude.includes(u.id));
  return (
    <Field className="gap-1">
      <FieldLabel htmlFor="access-user">Person</FieldLabel>
      <NativeSelect
        id="access-user"
        value={value}
        onChange={(e) => onChange(e.target.value, options.find((u) => u.id === e.target.value)?.name ?? "")}
        className="w-full"
      >
        <NativeSelectOption value="">{people.isPending ? "Loading…" : options.length ? "Choose a person…" : "Everyone already has access"}</NativeSelectOption>
        {options.map((u) => (
          <NativeSelectOption key={u.id} value={u.id}>
            {u.name} — {u.email}
          </NativeSelectOption>
        ))}
      </NativeSelect>
    </Field>
  );
}

function StorePicker({
  organizations,
  exclude,
  value,
  onChange,
}: {
  organizations: EntityRef[];
  exclude: string[];
  value: string;
  onChange: (id: string) => void;
}) {
  const [organizationId, setOrganizationId] = useState(organizations[0]?.id ?? "");
  const stores = useOrgStores(organizationId || undefined);
  const options = (stores.data?.items ?? []).filter((s) => !exclude.includes(s.id));
  if (!organizationId) return <p className="text-muted-foreground text-xs">This person doesn't belong to any organization.</p>;
  return (
    <>
      {organizations.length > 1 ? (
        <Field className="gap-1">
          <FieldLabel htmlFor="access-org">Organization</FieldLabel>
          <NativeSelect
            id="access-org"
            value={organizationId}
            onChange={(e) => {
              setOrganizationId(e.target.value);
              onChange("");
            }}
            className="w-full"
          >
            {organizations.map((o) => (
              <NativeSelectOption key={o.id} value={o.id}>
                {o.name}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </Field>
      ) : null}
      <Field className="gap-1">
        <FieldLabel htmlFor="access-store">Store</FieldLabel>
        <NativeSelect id="access-store" value={value} onChange={(e) => onChange(e.target.value)} className="w-full">
          <NativeSelectOption value="">{stores.isPending ? "Loading…" : options.length ? "Choose a store…" : "No other stores"}</NativeSelectOption>
          {options.map((s) => (
            <NativeSelectOption key={s.id} value={s.id}>
              {s.name}
              {s.location ? ` — ${s.location}` : ""}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </Field>
    </>
  );
}
